import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, TrendingUp, Users, Timer, Star } from "lucide-react";
import { LineReveal, Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { Marquee, PartnerMarquee } from "@/components/motion/Marquee";
import QuickEnquiry from "@/components/forms/QuickEnquiry";
import EMICalculator from "@/components/tools/EMICalculator";
import { PRODUCTS, PROCESS_STEPS, PARTNERS, FAQS, SITE, waLink } from "@/data/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1721930513258-887f010ef5a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHx0cnVjayUyMG9uJTIwbW91bnRhaW4lMjBoaWdod2F5fGVufDB8fHx8MTc4NDQ0NTk1Mnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <main data-testid="home-page" className="pt-16 md:pt-20">
      <Hero />
      <TrustedStrip />
      <Products />
      <Manifesto />
      <ProcessSection />
      <EMISection />
      <EligibilityTeaser />
      <SuccessSection />
      <TestimonialsSection />
      <PartnersSection />
      <BlogTeaser />
      <FAQSection />
      <FinalCTA />
    </main>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-14 pb-24 md:pb-40">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="chapter-index text-emerald-700 flex items-center gap-2"><Sparkles size={12}/> Rightway Finance</p>
            </Reveal>
            <h1 className="font-display mt-6 text-[13vw] leading-[0.88] tracking-[-0.03em] text-slate-900 md:text-[9vw] lg:text-[8.4vw]">
              <LineReveal delay={0.1}>Finance Made</LineReveal>
              <LineReveal delay={0.22}><span className="italic text-emerald-800">Simple.</span></LineReveal>
              <LineReveal delay={0.34}>Growth Made</LineReveal>
              <LineReveal delay={0.46}><span className="italic text-emerald-800">Possible.</span></LineReveal>
            </h1>
            <Reveal delay={0.7}>
              <p className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
                Commercial vehicle, business and personal loans engineered around Indian entrepreneurs. Serving borrowers across all India.
              </p>
            </Reveal>
            <Reveal delay={0.85}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/apply" data-testid="hero-apply" className="btn-line inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 py-4 text-sm font-medium transition-colors">
                  Apply for a Loan <ArrowRight size={16}/>
                </Link>
                <Link to="/emi-calculator" className="inline-flex items-center gap-2 rounded-full border border-slate-900/15 hover:border-slate-900/40 text-slate-900 px-7 py-4 text-sm transition-colors">
                  Calculate EMI
                </Link>
                <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-900/15 hover:border-emerald-500 text-slate-900 px-7 py-4 text-sm transition-colors">
                  WhatsApp Us
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.4}>
              <QuickEnquiry />
            </Reveal>
          </div>
        </div>
      </div>

      {/* Parallax editorial image band */}
      <div className="relative -mt-8 md:-mt-16 h-[46vh] md:h-[62vh] overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Truck on mountain highway" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[hsl(40_33%_98%)]"/>
        </motion.div>
        <div className="absolute bottom-6 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between gap-4">
            <p className="chapter-index text-white/80">— Serving all India</p>
            <p className="font-display text-white/90 italic text-2xl md:text-4xl max-w-xl text-right">{SITE.tagline}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustedStrip() {
  const items = ["Rightway Finance · Since 2011", "14 Loan Products", "3 Branches across Kutch", "51+ Lending Partners", "Transparent · No Hidden Fees"];
  return (
    <section className="relative py-6 border-y border-black/[0.06] bg-white">
      <PartnerMarquee items={items} />
    </section>
  );
}

function Products() {
  const featured = PRODUCTS.filter((p) => p.tag === "Featured");
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="chapter-index text-emerald-700">Products</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-3 max-w-3xl">Loans engineered for <span className="italic text-emerald-800">real</span> businesses.</h2>
        </div>
        <Link to="/products" className="btn-line inline-flex items-center gap-2 text-sm text-slate-900 border-b border-slate-900 pb-1 self-start md:self-auto">View all 14 products <ArrowRight size={14}/></Link>
      </div>

      <Stagger className="mt-14 grid md:grid-cols-3 gap-8">
        {featured.map((p) => (
          <motion.div key={p.slug} variants={staggerItem}>
            <Link to={`/products#${p.slug}`} className="group block rounded-3xl overflow-hidden bg-white border border-black/[0.06] hover:shadow-2xl hover:shadow-emerald-900/10 transition-all">
              <div className="relative aspect-[5/4] overflow-hidden clip-frame">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"/>
              </div>
              <div className="p-6">
                <p className="font-display text-2xl md:text-3xl text-slate-900 group-hover:text-emerald-800 transition-colors">{p.name}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                <p className="mt-5 text-sm inline-flex items-center gap-2 text-emerald-800">Explore <ArrowRight size={14}/></p>
              </div>
            </Link>
          </motion.div>
        ))}
      </Stagger>
    </section>
  );
}

function Manifesto() {
  const items = [
    { n: "01", t: "Transparent Pricing", d: "No hidden fees. Sanction letters spell out every rupee before you sign." },
    { n: "02", t: "Multi-lender Access", d: "51+ bank & NBFC partners. One application. Best-fit offer for your profile." },
    { n: "03", t: "Rooted in Kutch", d: "We understand the freight, agri and construction economies we finance." },
    { n: "04", t: "Relationship First", d: "Every borrower gets a named advisor. No call-centre run-arounds." },
  ];
  return (
    <section className="relative bg-[hsl(222_47%_11%)] text-white overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-40">
        <p className="chapter-index text-emerald-400">Why Rightway</p>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] mt-4 max-w-4xl">A finance partner built for the entrepreneurs who <span className="italic text-emerald-400">actually build India.</span></h2>

        <Stagger className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-14">
          {items.map((it) => (
            <motion.div key={it.n} variants={staggerItem} className="border-t border-white/15 pt-8">
              <p className="chapter-index text-emerald-400/90">— {it.n}</p>
              <h3 className="font-display text-3xl md:text-4xl mt-3">{it.t}</h3>
              <p className="mt-3 text-slate-300 max-w-md leading-relaxed">{it.d}</p>
            </motion.div>
          ))}
        </Stagger>
      </div>
      <div className="py-8 border-t border-white/10">
        <Marquee items={["Entrepreneurs first", "No guarantees, only guidance", "Built in Bhuj", "Financing India's arteries", "Since day one"]} speed={45} />
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="chapter-index text-emerald-700">Process</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3 max-w-3xl">8 steps. <span className="italic text-emerald-800">No surprises.</span></h2>
        </div>
        <Link to="/apply" className="btn-line inline-flex items-center gap-2 text-sm text-slate-900 border-b border-slate-900 pb-1">Start now <ArrowRight size={14}/></Link>
      </div>

      <Stagger className="mt-14 grid md:grid-cols-4 gap-6">
        {PROCESS_STEPS.map((s) => (
          <motion.div key={s.n} variants={staggerItem} className="group rounded-3xl border border-black/[0.06] bg-white p-6 hover:bg-emerald-50/60 transition-colors">
            <p className="font-mono text-xs tracking-widest text-emerald-700">— {s.n}</p>
            <p className="font-display text-2xl mt-3 text-slate-900">{s.t}</p>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
          </motion.div>
        ))}
      </Stagger>
    </section>
  );
}

function EMISection() {
  return (
    <section className="bg-white border-y border-black/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-5">
            <p className="chapter-index text-emerald-700">Tools</p>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Plan your EMI. <span className="italic text-emerald-800">Live.</span></h2>
            <p className="mt-4 text-slate-600 max-w-md leading-relaxed">Drag the sliders. Watch the numbers move in real time. No pop-ups. No sign-ups.</p>
            <Link to="/eligibility" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-900 border-b border-slate-900 pb-1">Check eligibility <ArrowRight size={14}/></Link>
          </div>
          <div className="md:col-span-7">
            <EMICalculator embedded />
          </div>
        </div>
      </div>
    </section>
  );
}

function EligibilityTeaser() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden aspect-[4/3] clip-frame">
          <img src="https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3ODQ0NDU5NDN8MA&ixlib=rb-4.1.0&q=85" alt="Business handshake" className="w-full h-full object-cover"/>
        </div>
        <div>
          <p className="chapter-index text-emerald-700">Eligibility</p>
          <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Am I <span className="italic text-emerald-800">eligible?</span></h2>
          <p className="mt-4 text-slate-600 max-w-md leading-relaxed">A two-minute self-check based on your income, existing EMIs and credit profile. Instant indicative result — no impact on your credit score.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/eligibility" data-testid="elig-cta" className="btn-line inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 text-sm">Check Eligibility <ArrowRight size={16}/></Link>
            <Link to="/track" className="inline-flex items-center gap-2 rounded-full border border-slate-900/15 hover:border-emerald-500 text-slate-900 px-6 py-3.5 text-sm">Track Application</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SuccessSection() {
  const stats = [
    { icon: Users, label: "Advisor-led applications", note: "Named advisor for every borrower" },
    { icon: Timer, label: "24-48h pre-approval turnaround", note: "With complete documentation" },
    { icon: TrendingUp, label: "Multi-lender routing", note: "Best fit picked from 51+ partners" },
    { icon: ShieldCheck, label: "Transparent sanction letters", note: "Every rupee spelled out upfront" },
  ];
  return (
    <section className="bg-white border-y border-black/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <p className="chapter-index text-emerald-700">Our Promise</p>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3 max-w-3xl">What our customers get. <span className="italic text-emerald-800">Always.</span></h2>
        <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="rounded-3xl border border-black/[0.06] p-6">
              <s.icon className="text-emerald-700" size={22}/>
              <p className="font-display text-2xl mt-4 leading-tight text-slate-900">{s.label}</p>
              <p className="mt-2 text-sm text-slate-500">{s.note}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500">Sample content — this section is designed to be replaced with real customer success once available. No fabricated numbers or approvals shown.</p>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const items = [
    { name: "Rakesh P.", role: "Fleet Owner, Bhuj", q: "The team walked me through every clause. Sanction to disbursal in under a week." },
    { name: "Meena S.", role: "MSME, Mundra", q: "Multi-lender access got me a rate 90 bps lower than my current bank. Zero paperwork chaos." },
    { name: "Iqbal K.", role: "Truck operator", q: "Refinance unlocked working capital when I needed it most. Named advisor made the difference." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <p className="chapter-index text-emerald-700">Testimonials</p>
      <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3 max-w-3xl italic">"They finance vehicles. They actually build entrepreneurs."</h2>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {items.map((t) => (
          <figure key={t.name} className="rounded-3xl border border-black/[0.06] bg-white p-6 md:p-8">
            <div className="flex gap-1 text-emerald-700">{Array.from({length:5}).map((_,i)=>(<Star key={i} size={14} fill="currentColor"/>))}</div>
            <blockquote className="font-display text-2xl leading-snug text-slate-900 mt-4">"{t.q}"</blockquote>
            <figcaption className="mt-6 text-sm text-slate-500">— {t.name}, {t.role}</figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-500">Sample testimonials — placeholder content for editorial layout. Live testimonials will replace these post-launch.</p>
    </section>
  );
}

function PartnersSection() {
  return (
    <section className="bg-white border-y border-black/[0.06] py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="chapter-index text-emerald-700">Partners</p>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mt-3">Partner Banks &amp; <span className="italic text-emerald-800">NBFCs</span></h2>
          </div>
          <p className="text-sm text-slate-600 max-w-md">A curated network of 51+ banks and NBFCs. Every application is routed to the best-fit lender.</p>
        </div>
      </div>
      <PartnerMarquee items={PARTNERS.map((p) => p.name)} />
    </section>
  );
}

function BlogTeaser() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="chapter-index text-emerald-700">Journal</p>
          <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Notes on <span className="italic text-emerald-800">borrowing well.</span></h2>
        </div>
        <Link to="/blog" className="btn-line inline-flex items-center gap-2 text-sm border-b border-slate-900 pb-1">Read the journal <ArrowRight size={14}/></Link>
      </div>
      <p className="mt-6 text-slate-600 max-w-2xl">Practical guides on truck loans, CIBIL, tractor financing, LAP and business growth — written for real Indian operators.</p>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-white border-y border-black/[0.06]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <p className="chapter-index text-emerald-700">FAQ</p>
        <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Straight <span className="italic text-emerald-800">answers.</span></h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="border-b border-black/10">
              <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display text-2xl md:text-3xl py-6 hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-40">
      <div className="rounded-[2.5rem] bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 text-white p-10 md:p-20 relative overflow-hidden grain">
        <p className="chapter-index text-emerald-200/80">The end · A new start</p>
        <h2 className="font-display text-5xl md:text-8xl leading-[0.92] mt-4 max-w-4xl">Let's finance <span className="italic">the next mile</span> of your business.</h2>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="btn-line inline-flex items-center gap-2 rounded-full bg-white text-emerald-800 px-7 py-4 text-sm font-medium hover:bg-emerald-50 transition-colors">Apply for a Loan <ArrowRight size={16}/></Link>
          <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/40 hover:border-white text-white px-7 py-4 text-sm">WhatsApp Us</a>
        </div>
      </div>
    </section>
  );
}
